import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import UserApi from '../api/userApi'
import { LocalStorageKeys } from '../constants'
import { endMoodleImpersonation } from '../hooks/api/useOpenMoodleLink'

  export interface CustomJwtPayload extends JwtPayload {
    [key: string]: any
    name?: string
    sub?: string
    picture?: string
    'https://eacq.app.com/roles': string[]
    'https://eacq.app.com/impersonateData'?: ImpersonateTokenData
    'https://eacq.app.com/isImpersonating'?: boolean
    permissions?: string[]
  }

  export interface ImpersonateTokenData {
    as: {
      email: string
      name: string
      picture: string
      user_id: string
    }
    at: string
    by: {
      email: string
      name: string
      user_id: string
    }
  }

  interface TokenClaims {
    'https://eacq.app.com/roles': string[]
    'https://eacq.app.com/impersonateData'?: ImpersonateTokenData
    'https://eacq.app.com/isImpersonating'?: boolean
    permissions?: string[]
    nickname: string
    name: string
    picture: string
    updated_at: string
    email: string
    email_verified: boolean
    iss: string
    aud: string
    sub: string
    iat: number
    exp: number
    sid: string
    nonce?: string
    [key: string]: any
  }

  const AUTH_CONFIG = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
    redirectUri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: 'openid profile email',
    cookieName: 'id_token',
    cookieMaxAge: 86400,
    cookieDomain: process.env.REACT_APP_COOKIE_DOMAIN || '.acq.org',
  }

  export const generateNonce = (): string => {
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  export const getClaimKeys = () => {
    return {
      IMPERSONATE_DATA_CLAIM: 'https://eacq.app.com/impersonateData',
      IS_IMPERSONATING_CLAIM: 'https://eacq.app.com/isImpersonating',
      ROLES_CLAIM: 'https://eacq.app.com/roles',
    }
  }

  export const isTokenExpired = (token: string): boolean => {
    try {
      const decoded = jwtDecode<CustomJwtPayload>(token)
      if (!decoded.exp) {
        return true
      }
      const currentTime = Math.floor(Date.now() / 1000)
      const isExpired = decoded.exp < currentTime
      return isExpired
    } catch (error) {
      return true
    }
  }

  export const getCookie = (name: string): string | null => {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))

    const result = cookie ? decodeURIComponent(cookie.split('=')[1]) : null
    return result
  }

  export const setCookie = (
    name: string,
    value: string,
    maxAge: number = AUTH_CONFIG.cookieMaxAge,
  ): void => {
    const domain = getCookieDomain()
    const isSecure = window.location.protocol === 'https:'

    let cookieString = `${name}=${encodeURIComponent(value)}; path=/; Max-Age=${maxAge}; SameSite=Lax`

    if (domain) {
      cookieString += `; domain=${domain}`
    }

    if (isSecure) {
      cookieString += '; Secure'
    }

    document.cookie = cookieString
  }

  export const clearCookie = (name: string): void => {
    const domain = getCookieDomain()
    const isSecure = window.location.protocol === 'https:'

    let cookieString = `${name}=; path=/; Max-Age=0`

    if (domain) {
      cookieString += `; domain=${domain}`
    }

    if (isSecure) {
      cookieString += '; Secure'
    }

    document.cookie = cookieString
  }

  export const getRootDomain = (hostname: string): string => {
    const parts = hostname.split('.')
    if (parts.length <= 2) return hostname

    const slicedParts = parts.slice(-3)
    if (['co', 'com'].includes(slicedParts[1])) {
      return slicedParts.join('.')
    }

    return parts.slice(-2).join('.')
  }

  export const getCookieDomain = (): string | null => {
    const hostname = window.location.hostname

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return null
    }

    if (AUTH_CONFIG.cookieDomain) {
      return AUTH_CONFIG.cookieDomain
    }

    if (hostname.includes('logientlab.com')) {
      return '.logientlab.com'
    }

    const rootDomain = `.${getRootDomain(hostname)}`
    return rootDomain
  }

  export const buildAuth0Url = (additionalParams: Record<string, string> = {}): string => {
    const { domain, clientId, redirectUri, scope, audience } = AUTH_CONFIG

    if (!domain || !clientId || !redirectUri) {
      throw new Error('Missing Auth0 configuration')
    } 

    const nonce = generateNonce()
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'id_token',
      scope,
      redirect_uri: redirectUri,
      nonce,
      ...(audience && { audience }),
      ...additionalParams,
    })
    const finalUrl = `https://${domain}/authorize?${params.toString()}`
    return finalUrl
  }

  export const buildImpersonationUrl = (userId: string): string => {
    if (!userId) {
      throw new Error('User ID is required for impersonation')
    }

    const url = buildAuth0Url({
      impersonate_user_id: userId,
    })

    return url
  }

  interface ImpersonationContextType {
    permissions: string[]
    roles: string[]
    userInfo: {
      nickname: string
      name: string
      picture: string
      email: string
      email_verified: boolean
      sub: string
    }

    isImpersonating: boolean
    originalUser: TokenClaims | null
    currentUser: TokenClaims | null

    hasImpersonationPermission: () => boolean
    canImpersonate: () => boolean
    getImpersonationRegionCode: () => string
    startImpersonation: (targetUserId: string) => Promise<void>
    stopImpersonation: () => void
    refreshTokenClaims: () => Promise<void>
    buildImpersonationUrl: (userId: string) => string

    isLoading: boolean
    error: string | null
  }

  const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined)

  interface ImpersonationProviderProps {
    children: ReactNode
  }

  export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
    const {
      getAccessTokenSilently,
      getIdTokenClaims,
      isAuthenticated,
      isLoading: auth0Loading,
      logout,
    } = useAuth0()

    const [tokenClaims, setTokenClaims] = useState<TokenClaims | null>(null)
    const [isImpersonating, setIsImpersonating] = useState(false)
    const [originalUser, setOriginalUser] = useState<TokenClaims | null>(null)
    const [currentUser, setCurrentUser] = useState<TokenClaims | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const decodeToken = (token: string): TokenClaims | null => {
      try {
        const decoded = jwtDecode<TokenClaims>(token)
        return decoded
      } catch (err) {
        console.error('Error decoding JWT token:', err)
        setError('Failed to decode token')
        return null
      }
    }

    const refreshTokenClaims = async (): Promise<void> => {
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Priority 1: Check for delegation token in localStorage (personnification active)
        const delegationToken = localStorage.getItem('delegation_id_token') || localStorage.getItem('delegation_auth_token')
        const isImpersonatingFlag = localStorage.getItem('is_impersonating') === 'true'

        if (delegationToken && isImpersonatingFlag && !isTokenExpired(delegationToken)) {
          
          const claims = decodeToken(delegationToken)
          if (claims) {
            setTokenClaims(claims)
            setIsImpersonating(true)
            setCurrentUser(claims)

            // Load original user from Auth0 cookie/token (fallback if not in localStorage)
            if (!originalUser) {
              try {
                const idTokenClaims = await getIdTokenClaims()
                if (idTokenClaims?.__raw) {
                  const staffClaims = decodeToken(idTokenClaims.__raw)
                  if (staffClaims) {
                    setOriginalUser(staffClaims)
                  }
                }
              } catch (err) {
                console.warn('[IMPERSONATION] Could not load original user claims:', err)
              }
            }

            setIsLoading(false)
            return
          }
        }

        // Priority 2: Check cookie token
        const cookieToken = getCookie(AUTH_CONFIG.cookieName)
        let tokenToProcess: string | null = null

        if (cookieToken && !isTokenExpired(cookieToken)) {
          tokenToProcess = cookieToken

          setCookie(AUTH_CONFIG.cookieName, cookieToken)
        } else {
          // Priority 3: Get tokens from Auth0
          const idTokenClaims = await getIdTokenClaims()

          if (idTokenClaims?.__raw) {
            tokenToProcess = idTokenClaims.__raw
          }
        }

        if (tokenToProcess) {
          const claims = decodeToken(tokenToProcess)
          if (claims) {
            setTokenClaims(claims)

            const { IMPERSONATE_DATA_CLAIM, IS_IMPERSONATING_CLAIM } = getClaimKeys()
            const impersonateData = claims[IMPERSONATE_DATA_CLAIM] as ImpersonateTokenData | undefined
            const isImpersonatingFlagFromToken = claims[IS_IMPERSONATING_CLAIM] as boolean | undefined

            if (isImpersonatingFlagFromToken && impersonateData) {
              setIsImpersonating(true)
              if (!originalUser) {
                setOriginalUser(claims)
              }
            } else {
              setIsImpersonating(false)
              setOriginalUser(claims)
            }

            setCurrentUser(claims)
          }
        }
      } catch (err) {
        console.error('Error refreshing token claims:', err)
        setError('Failed to refresh token claims')
      } finally {
        setIsLoading(false)
      }
    }

    const hasImpersonationPermission = (): boolean => {
      const permissions = currentUser?.permissions || []
      return permissions.includes('impersonate:users')
    }

    const canImpersonate = (): boolean => {
      const roles = currentUser?.['https://eacq.app.com/roles'] || []
      const allowedRoles = ['Impersonate', 'admin_province', 'admin_region']
      // here use hasImpersonationPermission() when the token claims are updated
      return  roles.some(role => allowedRoles.includes(role))
    }

    const getImpersonationRegionCode = (): string => {
      const roles = currentUser?.['https://eacq.app.com/roles'] || []
      // Récupérer le vrai code de région depuis localStorage (ex: QC, ON, etc.)
      let activeRegionCode = localStorage.getItem(LocalStorageKeys.ActiveRegionCode)
      
      
      // Fallback: si localStorage est vide, essayer d'extraire du cookie REGIONAL_ACQ
      // On supposera que si REGIONAL_ACQ vaut un numéro, on doit retourner 99
      if (!activeRegionCode) {
        const regionalAcq = getCookie('REGIONAL_ACQ')
        
        activeRegionCode = regionalAcq
      }
      
      // Si l'utilisateur a un code de région valide (pas 99), l'utiliser
      if (activeRegionCode && activeRegionCode !== '99') {
        // Vérifier si l'utilisateur a le rôle admin_region OU Impersonate
        if (roles.includes('admin_region') || roles.includes('Impersonate')) {
          return activeRegionCode
        }
      }
      
      // Par défaut, retourner 99 (tous les utilisateurs - admin_province)
      return '99'
    }

    const startImpersonation = async (targetUserId: string): Promise<void> => {
      try {
        setError(null)

        if (!canImpersonate()) {
          throw new Error('User does not have permission to impersonate')
        }

        if (!targetUserId) {
          throw new Error('Target user ID is required')
        }

        if (!isImpersonating && currentUser) {
          setOriginalUser(currentUser)
        }

        // Call T3 API to start personnification
        const token = await getAccessTokenSilently()

        const apiBaseUrl = (process.env.REACT_APP_URL_API || '').endsWith('/')
          ? process.env.REACT_APP_URL_API
          : `${process.env.REACT_APP_URL_API}/`
        const response = await fetch(`${apiBaseUrl}personnification/start`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY || '',
            'target-user-id': targetUserId
          },
          body: JSON.stringify({ contact_id: targetUserId })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error?.message || `HTTP ${response.status}`)
        }

        const data = await response.json()

        if (!data.success || !data.data) {
          throw new Error('Invalid response from server')
        }

        // Store delegation token in localStorage
        const { delegation_token, impersonate_data, contact } = data.data

        localStorage.setItem('delegation_auth_token', delegation_token.access_token)
        if (delegation_token.id_token) {
          localStorage.setItem('delegation_id_token', delegation_token.id_token)
        }
        localStorage.setItem('impersonate_data', JSON.stringify(impersonate_data))
        localStorage.setItem('impersonated_contact', JSON.stringify(contact))
        localStorage.setItem('impersonated_user_id', targetUserId)
        localStorage.setItem('is_impersonating', 'true')
        localStorage.setItem('delegation_token_expires_at', String(Date.now() + (delegation_token.expires_in * 1000)))

        // Create shared cookie for WordPress to read delegation token
        setCookie('delegation_auth_token', delegation_token.access_token, delegation_token.expires_in)
        setCookie('is_impersonating', 'true', delegation_token.expires_in)
        setCookie('impersonated_user_id', targetUserId, delegation_token.expires_in)

        // Redirect to profile page to apply new token context
        setTimeout(() => {
          window.location.href = '/profile/personal-informations'
        }, 100)

      } catch (err) {
        console.error('Error starting impersonation:', err)
        setError(err instanceof Error ? err.message : 'Failed to start impersonation')
        throw err
      }
    }

    const stopImpersonation = async (): Promise<void> => {
      try {
        setError(null)

        if (!isImpersonating) {
          return
        }

        // Get delegation token from localStorage
        const delegationToken = localStorage.getItem('delegation_auth_token')

        if (delegationToken) {
          try {
            // Call T4 endpoint to invalidate token server-side
            const apiBaseUrl = (process.env.REACT_APP_URL_API || '').endsWith('/')
              ? process.env.REACT_APP_URL_API
              : `${process.env.REACT_APP_URL_API}/`
            const response = await fetch(`${apiBaseUrl}personnification/stop`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${delegationToken}`,
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_API_KEY || ''
              },
              body: JSON.stringify({})
            })

            if (!response.ok) {
              console.warn('Failed to stop personnification on server, continuing with cleanup')
            }
          } catch (apiError) {
            console.warn('Error calling stop endpoint:', apiError)
            // Continue with cleanup even if API call fails
          }
        }

        // Clear Auth0 cookie
        clearCookie(AUTH_CONFIG.cookieName)

        // Clear WordPress delegation cache via API
        try {
          console.log('[IMPERSONATION] Calling WordPress API to clear delegation cache...')
          const wordpressUrl = process.env.REACT_APP_ACQ_SITE
          const apiUrl = `${wordpressUrl}/wp-json/acq/v1/clear-delegation-cache`
          console.log('[IMPERSONATION] API URL:', apiUrl)
          
          const wpResponse = await fetch(apiUrl, {
            method: 'POST',
            credentials: 'include', // Include WordPress auth cookies
            headers: {
              'Content-Type': 'application/json'
            }
          })

          console.log('[IMPERSONATION] WordPress API response status:', wpResponse.status)
          
          if (wpResponse.ok) {
            const responseData = await wpResponse.json()
            console.log('[IMPERSONATION] ✅ WordPress delegation cache cleared successfully:', responseData)
          } else {
            const errorText = await wpResponse.text()
            console.warn('[IMPERSONATION] ⚠️ Failed to clear WordPress cache:', wpResponse.status, errorText)
          }
        } catch (wpError) {
          console.error('[IMPERSONATION] ❌ Error clearing WordPress cache:', wpError)
          // Continue with cleanup even if WordPress call fails
        }

        // Update session (optional, for backwards compatibility)
        try {
          const token = await getAccessTokenSilently()
          await UserApi.updateSession(null, {
            headers: { Authorization: `Bearer ${token}` },
          })
        } catch (apiError) {
          console.warn('Failed to update session during logout:', apiError)
        }

        // Close the impersonated Moodle session, if one was opened (ticket 5277)
        await endMoodleImpersonation()

        // Clear all localStorage (including delegation tokens)
        localStorage.clear()

        // Clear delegation cookies shared with WordPress
        clearCookie('delegation_auth_token')
        clearCookie('is_impersonating')
        clearCookie('impersonated_user_id')

        // Force Auth0 logout to clear session and redirect to home
        logout({ 
          logoutParams: { 
            returnTo: window.location.origin 
          } 
        })
      } catch (err) {
        console.error('Error stopping impersonation:', err)
        setError(err instanceof Error ? err.message : 'Failed to stop impersonation')
        throw err
      }
    }

    useEffect(() => {
      const handleCallback = () => {
        if (window.location.hash && window.location.hash.includes('id_token')) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1))
          const processed = handleAuth0Callback(hashParams)

          if (processed) {
            setTimeout(() => {
              refreshTokenClaims()
            }, 100)
          }
        }
      }

      handleCallback()
    }, [])

    useEffect(() => {
      if (isAuthenticated && !auth0Loading) {
        refreshTokenClaims()
      } else if (!isAuthenticated) {
        setTokenClaims(null)
        setCurrentUser(null)
        setOriginalUser(null)
        setIsImpersonating(false)
        setIsLoading(false)
      }
    }, [isAuthenticated, auth0Loading])

    const permissions = currentUser?.permissions || []
    const roles = currentUser?.['https://eacq.app.com/roles'] || []
    
    const getEffectiveUserInfo = () => {
      if (!currentUser) {
        return {
          nickname: '',
          name: '',
          picture: '',
          email: '',
          email_verified: false,
          sub: '',
        }
      }

      if (isImpersonating && currentUser['https://eacq.app.com/impersonateData']) {
        const impersonateData = currentUser['https://eacq.app.com/impersonateData']
        return {
          nickname: impersonateData.as.name || '',
          name: impersonateData.as.name || '',
          picture: impersonateData.as.picture || '',
          email: impersonateData.as.email || '',
          email_verified: true,
          sub: impersonateData.as.user_id || '',
        }
      }

      return {
        nickname: currentUser.nickname || '',
        name: currentUser.name || '',
        picture: currentUser.picture || '',
        email: currentUser.email || '',
        email_verified: currentUser.email_verified || false,
        sub: currentUser.sub || '',
      }
    }

    const userInfo = getEffectiveUserInfo()

    const contextValue: ImpersonationContextType = {
      permissions,
      roles,
      userInfo,

      isImpersonating,
      originalUser,
      currentUser,

      hasImpersonationPermission,
      canImpersonate,
      getImpersonationRegionCode,
      startImpersonation,
      stopImpersonation,
      refreshTokenClaims,
      buildImpersonationUrl,

      isLoading: isLoading || auth0Loading,
      error,
    }

    return (
      <ImpersonationContext.Provider value={contextValue}>{children}</ImpersonationContext.Provider>
    )
  }

  export const handleAuth0Callback = (searchParams: URLSearchParams): boolean => {
    const idToken = searchParams.get('id_token')
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (error) {
      console.error('[IMPERSONATION] Auth0 callback error:', { error, errorDescription })
      return false
    }

    if (!idToken) {
      return false
    }

    setCookie(AUTH_CONFIG.cookieName, idToken)

    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }

    return true
  }

  export const useImpersonation = (): ImpersonationContextType => {
    const context = useContext(ImpersonationContext)
    if (context === undefined) {
      throw new Error('useImpersonation must be used within an ImpersonationProvider')
    }
    return context
  }

  export default ImpersonationContext